"use client";

import Link from "next/link";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { ChevronRight, MapPin } from "lucide-react";
import { useTranslations } from "next-intl";

type Variant = "solid" | "link";

type Props = {
  src?: string;
  label?: string;
  variant?: Variant;
  className?: string;
  iconClassName?: string;
  children?: React.ReactNode;
};

const VARIANT_CLASSNAMES: Record<Variant, string> = {
  solid:
    "inline-flex items-center gap-2 bg-[#ffb800] hover:bg-[#ffc933] text-[#102542] px-4 py-2 rounded-lg font-semibold text-sm shadow-md hover:shadow-lg transition-all",
  link: "inline-flex items-center gap-1.5 self-start text-sm font-semibold text-[#102542] hover:text-[#ffb800] hover:gap-2 transition-all duration-300",
};

export default function MapButton({
  src,
  label,
  variant = "solid",
  className,
  iconClassName,
  children,
}: Props) {
  const { locale } = useParams<{ locale: string }>();
  const t = useTranslations("common");
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const fromUrl = searchParams.toString()
    ? `${pathname}?${searchParams.toString()}`
    : pathname;

  const params = new URLSearchParams();
  if (src) params.set("src", src);
  params.set("from", fromUrl);

  const href = `/${locale}/map?${params.toString()}`;
  const defaultLabel = t("verEnMapa");

  return (
    <Link href={href} className={className ?? VARIANT_CLASSNAMES[variant]}>
      {children ?? (
        <>
          <MapPin className={iconClassName ?? "w-4 h-4"} />
          {label ?? defaultLabel}
          {variant === "link" && <ChevronRight className="w-4 h-4" />}
        </>
      )}
    </Link>
  );
}
